<template>
  <div :class="classes()">
    <div :class="`${el}__header`">
      <ErrorModal404 v-if="$route.query.statusCode === '404'" isCareLevelError />
      <Container size="m">
        <div>
          <Headline v-if="$matchMedia.l" :heading="1" size="s" centered noMargin>
            {{ $t('careLevelCalculator.landingPage.header.headline') }}
          </Headline>
          <Copy v-else element="div" size="xl" color="primaryBlue" bold centered>
            {{ $t('careLevelCalculator.landingPage.header.headline') }}
          </Copy>
        </div>
        <Headline
          :staticClass="`${el}__headerSubline`"
          :heading="2"
          :size="$matchMedia.l ? 'xl' : 'm'"
          centered
        >
          {{ $t('careLevelCalculator.landingPage.header.subline') }}
        </Headline>

        <div :class="`${el}__formBox`">
          <Headline
            :staticClass="`${el}__headerFormBoxHeadline`"
            :heading="3"
            size="xs"
            responsive
            centered
            noMargin
          >
            {{ $t('careLevelCalculator.landingPage.intro.headline') }}
          </Headline>

          <div :class="`${el}__formBoxRow`">
            <BaseRadio
              slot="0"
              id="home-radio-1"
              name="home-radio"
              :label="$t('careLevelCalculator.preWizard.step1.radio1.label')"
              checkmark
              advanced
              :checked="dependentPerson === 'other'"
              @input="onChange('other')"
              tabindex="0"
              @keyup.enter.native="onChange('other')"
              @keyup.space.native="onChange('other')"
              role="radio"
              :aria-checked="dependentPerson === 'other'"
              :error="error"
            />
            <BaseRadio
              slot="1"
              id="home-radio-2"
              name="home-radio"
              :label="$t('careLevelCalculator.preWizard.step1.radio2.label')"
              checkmark
              advanced
              :checked="dependentPerson === 'me'"
              @input="onChange('me')"
              tabindex="0"
              @keyup.enter.native="onChange('me')"
              @keyup.space.native="onChange('me')"
              role="radio"
              :aria-checked="dependentPerson === 'me'"
              :error="error"
            />
            <div :class="`${el}__formBoxButton`">
              <Button
                @click="onClick"
                :label="$t('careLevelCalculator.landingPage.intro.button.label')"
                additionalIconName="arrow-right-line"
                :expand="!$matchMedia.l"
              />
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div :class="`${el}__headerBenefits`">
          <div :class="`${el}__headerBenefitsImage`">
            <Picture
              :img="$t('careLevelCalculator.landingPage.device.v1.image')"
              alt=""
              responsive
            />
          </div>
          <div :class="`${el}__headerBenefitsBullets`">
            <UnorderedList
              iconName="checkbox-circle-line"
              :listItems="$t('careLevelCalculator.landingPage.header.bulletpoints')"
              horizontal
              bold
            />
          </div>
        </div>
      </Container>
    </div>
    <div :class="`${el}__triangle`" />
    <div v-if="$matchMedia.l" :class="`${el}__space`" />

    <section :class="`${el}__section ${el}__section--onlyTop`">
      <Container size="m">
        <Headline :size="$matchMedia.l ? 'l' : 's'" responsive centered>
          {{ $t('careLevelCalculator.landingPage.benefits.headline') }}
        </Headline>

        <div :class="`${el}__sectionInner`">
          <Row>
            <Col :xs="12" :m="6" hasMargin>
              <div :class="`${el}__benefit`">
                <Headline size="xs" responsive noMargin>
                  {{ $t('careLevelCalculator.landingPage.benefits.box1.headline') }}
                </Headline>

                <Divider class="u-margin__vertical--3" dotted />

                <Copy
                  v-for="(copy, idx) in $t('careLevelCalculator.landingPage.benefits.box1.copies')"
                  :key="`copyBenefits-${idx}`"
                >
                  {{ copy }}
                </Copy>

                <div class="u-margin__vertical--3" />

                <Alert
                  :headline="
                    $t('careLevelCalculator.landingPage.benefits.box1.notice', {
                      value: formatNumber(maxTotalPerYear(), 0),
                    })
                  "
                  background="secondaryMintLight"
                />
              </div>
            </Col>
            <Col :xs="12" :m="6" hasMargin>
              <div :class="`${el}__benefit`">
                <Headline size="xs" responsive noMargin>
                  {{ $t('careLevelCalculator.landingPage.benefits.box2.headline') }}
                </Headline>

                <Divider class="u-margin__vertical--3" dotted />

                <UnorderedList
                  iconName="arrow-right-s-line"
                  iconColor="tertiaryDark"
                  :listItems="$t('careLevelCalculator.landingPage.benefits.box2.bullets')"
                  bold
                />
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </section>

    <section :class="`${el}__section`">
      <Container size="m">
        <Headline :size="$matchMedia.l ? 'l' : 's'" responsive centered>
          {{ $t('careLevelCalculator.landingPage.trust.headline') }}
        </Headline>
      </Container>
      <Container>
        <Trustpilot type="slider" :class="`${el}__trustpilot`" />
        <div :class="`${el}__tuev`">
          <TuevCertificate />
        </div>
      </Container>
    </section>
  </div>
</template>

<script>
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import Headline from '@/objects/Headline';
import Button from '@/objects/Button';
import Divider from '@/objects/Divider';
import Picture from '@/objects/Picture';
import BaseRadio from '@/objects/Forms/BaseRadio';
import Alert from '@/objects/Alert';
import Copy from '@/objects/Copy';
import UnorderedList from '@/objects/UnorderedList';
import TuevCertificate from '@/objects/TuevCertificate';
import Trustpilot from '@/objects/Trustpilot';
import Container from '@/objects/Container/Container';

import {
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD,
  ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD,
} from '@/constants/routes';
import { mapFieldsHealth, mapFieldsAuth } from '@/store/mapFields';
import { createTrackingData } from '@/helpers/tracking/utils/helpers';
import { formatNumber } from '@/helpers/formatter';

import careServiceFacts, { serviceInfos } from '@/content/services/CareService/CareServiceFacts';
import ErrorModal404 from '@/components/ErrorModal404/ErrorModal404';

export default {
  name: 'CareLevelCalculatorIndex',
  props: {},
  components: {
    ErrorModal404,
    Container,
    Col,
    Row,
    Alert,
    Copy,
    BaseRadio,
    TuevCertificate,
    Trustpilot,
    Headline,
    Button,
    Picture,
    UnorderedList,
    Divider,
  },
  computed: {
    ...mapFieldsHealth(['dependentPerson']),
    ...mapFieldsAuth(['isAuthenticated']),
  },
  created() {
    if (this.isAuthenticated) {
      this.$router.push(ROUTE_SERVICE_CARELEVEL_CALCULATOR_WIZARD);
    }
  },
  data() {
    return {
      formatNumber,
      error: false,
      ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD,
      el: 'v-careLevelCalculatorIndex',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  methods: {
    maxTotalPerYear() {
      const facts = careServiceFacts[careServiceFacts.length - 1];
      let value = 0;

      Object.keys(facts).map((c) => {
        if (c !== 'pflegegrad' && c !== 'pflegegeld') {
          const { payout } = serviceInfos.filter((s) => s.label === c)[0];
          value += payout === 'monthly' ? facts[c] * 12 : facts[c];
        }
      });

      return value;
    },

    onChange(value) {
      this.error = false;
      this.dependentPerson = value;
    },

    onClick() {
      if (this.dependentPerson) {
        localStorage.setItem('dependentPerson', this.dependentPerson);
        this.$router.push(ROUTE_SERVICE_CARELEVEL_CALCULATOR_PREWIZARD);
        createTrackingData(
          'Pflegegradrechner',
          'Pflegegradrechner-Start-Content_btn-' +
            this.$t('careLevelCalculator.landingPage.intro.button.label'),
          'button'
        );
      } else {
        this.error = true;
      }
    },
  },
};
</script>
